// CTV-445: shared time-trend bucketing for the on-page "plays by …" chart and
// the PDF's delivery bar chart, so both pick the same granularity and bucket the
// daily data identically (no drift between report + export).

import { formatShortDate } from './shared/dateFormat';

export type TrendView = 'day' | 'week' | 'month';

export interface TrendDatum {
  date: string; // YYYY-MM-DD
  plays: number;
  completes: number;
}

export interface TrendBucket {
  key: string;
  label: string;
  plays: number;
  completes: number;
}

// Sunday-start week key (YYYY-MM-DD), matching the client-facing TBN report.
export function weekStartKey(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - dt.getDay()); // back up to Sunday
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

// Each day as its own bucket (no aggregation).
export function bucketByDay(daily: TrendDatum[], withYear = false): TrendBucket[] {
  return daily.map((d) => ({ key: d.date, label: formatShortDate(d.date, withYear), plays: d.plays, completes: d.completes }));
}

export function bucketByWeek(daily: TrendDatum[], withYear = false): TrendBucket[] {
  const map = new Map<string, { plays: number; completes: number }>();
  for (const d of daily) {
    const k = weekStartKey(d.date);
    const cur = map.get(k) ?? { plays: 0, completes: 0 };
    cur.plays += d.plays;
    cur.completes += d.completes;
    map.set(k, cur);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, v]) => ({ key, label: formatShortDate(key, withYear), plays: v.plays, completes: v.completes }));
}

// Calendar-month buckets (YYYY-MM), e.g. "May 2026".
export function bucketByMonth(daily: TrendDatum[], withYear = false): TrendBucket[] {
  const map = new Map<string, { plays: number; completes: number }>();
  for (const d of daily) {
    const k = d.date.slice(0, 7); // YYYY-MM
    const cur = map.get(k) ?? { plays: 0, completes: 0 };
    cur.plays += d.plays;
    cur.completes += d.completes;
    map.set(k, cur);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, v]) => {
      const [y, m] = key.split('-').map(Number);
      const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
      return { key, label, plays: v.plays, completes: v.completes };
    });
}

export function bucketByView(daily: TrendDatum[], view: TrendView, withYear: boolean): TrendBucket[] {
  if (view === 'day') return bucketByDay(daily, withYear);
  if (view === 'month') return bucketByMonth(daily, withYear);
  return bucketByWeek(daily, withYear);
}

// Default the trend to the finest granularity whose bar count fits, so a long
// campaign doesn't open on a wall of daily bars. Each bucket draws TWO bars
// (plays + completes) in the report, so the cap is on bucket-count × 2.
export const MAX_DEFAULT_BARS = 65;
export function pickDefaultView(daily: TrendDatum[] | null): TrendView {
  if (!daily || daily.length === 0) return 'week';
  const fits = (buckets: number) => buckets * 2 <= MAX_DEFAULT_BARS;
  if (fits(daily.length)) return 'day';
  if (fits(new Set(daily.map((d) => weekStartKey(d.date))).size)) return 'week';
  return 'month';
}

// "Daily" / "Weekly" / "Monthly" for the chart title/caption.
export function viewAdverbial(view: TrendView): string {
  return view === 'day' ? 'Daily' : view === 'month' ? 'Monthly' : 'Weekly';
}
